import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Heart, ChevronRight, Sparkles, Loader2 } from "lucide-react";
import { CATEGORIES } from "@/data/boutique";
import {
    SHOPIFY_CONFIGURED,
    fetchAllProducts,
    fetchCollectionProducts,
} from "@/lib/shopify";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeHandle = searchParams.get("collection") || "all";

    const [products, setProducts] = useState([]);
    const [collection, setCollection] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        if (!SHOPIFY_CONFIGURED) {
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        const task =
            activeHandle === "all"
                ? fetchAllProducts(36).then((items) => ({ items, col: null }))
                : fetchCollectionProducts(activeHandle, 36).then((col) => ({
                      items: col?.products || [],
                      col,
                  }));
        task
            .then(({ items, col }) => {
                if (cancelled) return;
                setProducts(items);
                setCollection(col);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message || "Could not load products");
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [activeHandle]);

    const setActive = (handle) => {
        if (handle === "all") {
            setSearchParams({});
        } else {
            setSearchParams({ collection: handle });
        }
    };

    return (
        <div data-testid="shop-page" className="cream-textured">
            {/* Page header */}
            <section className="relative py-16 md:py-20 text-center sparkle-overlay">
                <div className="relative z-10 max-w-3xl mx-auto px-6">
                    <p className="font-script text-3xl text-bb-pink-script">Shop</p>
                    <h1 className="font-serif-display text-4xl md:text-6xl tracking-[0.1em] uppercase text-bb-text">
                        {collection ? collection.title : "Boutique Collections"}
                    </h1>
                    <div className="mt-3 flex items-center justify-center gap-3 text-bb-rose-gold">
                        <span className="swoosh w-14" />
                        <Heart size={14} className="fill-current text-bb-pink-script animate-heart-beat" />
                        <span className="swoosh w-14" />
                    </div>
                    <p className="mt-5 text-bb-text-soft">
                        {collection?.description ||
                            "Faith-based, woman-owned western boutique — handpicked pieces to inspire & encourage."}
                    </p>
                </div>
            </section>

            {/* Collection filter chips */}
            <section className="pb-2 pt-2">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3" data-testid="collection-filter">
                        <button
                            onClick={() => setActive("all")}
                            data-testid="collection-chip-all"
                            className={`px-4 py-2 rounded-full text-xs tracking-[0.2em] uppercase border transition-all duration-300 ${
                                activeHandle === "all"
                                    ? "bg-bb-pink-script text-white border-bb-pink-script"
                                    : "bg-white text-bb-text border-bb-warm-brown/30 hover:border-bb-pink-script hover:text-bb-pink-script"
                            }`}
                        >
                            All
                        </button>
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.slug}
                                onClick={() => setActive(c.slug)}
                                data-testid={`collection-chip-${c.slug}`}
                                className={`px-4 py-2 rounded-full text-xs tracking-[0.2em] uppercase border transition-all duration-300 ${
                                    activeHandle === c.slug
                                        ? "bg-bb-pink-script text-white border-bb-pink-script"
                                        : "bg-white text-bb-text border-bb-warm-brown/30 hover:border-bb-pink-script hover:text-bb-pink-script"
                                }`}
                            >
                                {c.title}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Product grid */}
            <section className="py-12 md:py-16 relative overflow-hidden">
                <div className="leopard-blob leopard-blob--left" aria-hidden="true" />
                <div className="leopard-blob leopard-blob--right" aria-hidden="true" />
                <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                    {!SHOPIFY_CONFIGURED && (
                        <div className="text-center py-16 text-bb-text-soft">
                            Shop is connecting to Shopify — please check back shortly.
                        </div>
                    )}

                    {SHOPIFY_CONFIGURED && loading && (
                        <div className="flex flex-col items-center justify-center py-20 text-bb-text-soft" data-testid="shop-loading">
                            <Loader2 size={28} className="animate-spin text-bb-pink-script" />
                            <p className="mt-3 text-sm tracking-[0.2em] uppercase">Loading boutique</p>
                        </div>
                    )}

                    {SHOPIFY_CONFIGURED && !loading && error && (
                        <div className="text-center py-16" data-testid="shop-error">
                            <p className="text-bb-text">{error}</p>
                            <p className="mt-2 text-sm text-bb-text-soft">
                                Please try again later or visit our Shopify store directly.
                            </p>
                        </div>
                    )}

                    {SHOPIFY_CONFIGURED && !loading && !error && products.length === 0 && (
                        <div className="text-center py-16" data-testid="shop-empty">
                            <Sparkles size={22} className="mx-auto text-bb-pink-script" />
                            <p className="mt-3 font-serif-display text-xl text-bb-text">
                                New arrivals on the way
                            </p>
                            <p className="mt-2 text-sm text-bb-text-soft max-w-md mx-auto">
                                We're stocking this collection now. Check back soon or get notified.
                            </p>
                            <div className="mt-6">
                                <Link to="/contact" className="btn-blush">Notify Me</Link>
                            </div>
                        </div>
                    )}

                    {SHOPIFY_CONFIGURED && !loading && products.length > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-7" data-testid="shop-grid">
                            {products.map((p, i) => (
                                <div key={p.id} style={{ transitionDelay: `${i * 60}ms` }}>
                                    <ProductCard product={p} testIdPrefix="shop-product" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA banner */}
            <section className="bg-bb-blush-2 py-12 text-center">
                <div className="max-w-2xl mx-auto px-6 fade-up">
                    <h2 className="font-serif-display tracking-[0.18em] uppercase text-xl md:text-2xl text-bb-text">
                        Looking for something specific?
                    </h2>
                    <p className="mt-3 text-bb-text-soft leading-relaxed">
                        Send us a message — we'd love to help you find the perfect blessing.
                    </p>
                    <div className="mt-5">
                        <Link to="/contact" className="btn-black btn-shine" data-testid="shop-contact-cta">
                            Contact Us <ChevronRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
